@import "uniwind";
@import "tailwindcss";
@import "tw-animate-css";

@custom-variant dark (&:is(.dark *));
@custom-variant fixed (&:is(.layout-fixed *));

@theme inline {
  --breakpoint-3xl: 1600px;
  --breakpoint-4xl: 2000px;
  --font-sans: "DMSans_400Regular";
  --font-mono: var(--font-mono);
  --font-display: "ClashDisplay";
  --radius-sm: calc(var(--radius) - 4px);
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: var(--radius);
  --radius-xl: calc(var(--radius) + 4px);
  --radius-2xl: calc(var(--radius) + 8px);
  --radius-3xl: calc(var(--radius) + 12px);
  --radius-4xl: calc(var(--radius) + 16px);
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-card-event: var(--card-event);
  --color-border-card-event: var(--border-card-event);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-destructive-foreground: var(--destructive-foreground);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
  --color-chart-1: var(--chart-1);
  --color-chart-2: var(--chart-2);
  --color-chart-3: var(--chart-3);
  --color-chart-4: var(--chart-4);
  --color-chart-5: var(--chart-5);
  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-sidebar-primary: var(--sidebar-primary);
  --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
  --color-sidebar-accent: var(--sidebar-accent);
  --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
  --color-sidebar-border: var(--sidebar-border);
  --color-sidebar-ring: var(--sidebar-ring);
  --color-surface: var(--surface);
  --color-surface-foreground: var(--surface-foreground);
  --color-code: var(--code);
  --color-code-foreground: var(--code-foreground);
  --color-code-highlight: var(--code-highlight);
  --color-code-number: var(--code-number);
  --color-selection: var(--selection);
  --color-selection-foreground: var(--selection-foreground);
  --color-brand: var(--brand);
  --color-brand-foreground: var(--brand-foreground);
  --color-pink: var(--pink);
  --color-event-date: var(--event-date);
  --color-tag-foreground: var(--tag-foreground);
  --color-surface-container: var(--surface-container);
  --color-on-surface-variant: var(--on-surface-variant);
}

:root {
  --radius: 0.625rem;
  /* App background: near-black */
  --background: oklch(0.09 0 0);
  /* Primary text: soft off-white */
  --foreground: oklch(0.96 0.02 260);
  /* Cards/surfaces: dark gray on black */
  --card: oklch(0.16 0.01 260);
  --card-foreground: oklch(0.97 0.02 260);
  /* Event card background: slightly lighter for elevation */
  --card-event: oklch(0.2086 0.0019 106.57);
  /* Event card border: subtle outline */
  --border-card-event: oklch(0.32 0.015 260 / 40%);
  --popover: oklch(0.2086 0.0019 106.57);
  --popover-foreground: oklch(0.97 0.02 260);
  /* Primary action: neon lime (bottom tab, CTAs, favorites) */
  --primary: oklch(0.9347 0.1739 127.82);
  --primary-foreground: oklch(0.09 0 0);
  /* Secondary action: neutral dark gray pill */
  --secondary: oklch(0.2 0.01 260);
  --secondary-foreground: oklch(0.9 0.02 260);
  /* Muted surfaces for chips / text */
  --muted: oklch(0.18 0.01 260);
  /* Secondary text: soft gray */
  --muted-foreground: oklch(0.7 0.02 260);
  /* Accent for subtle pills, filters, tags */
  --accent: oklch(0.3312 0.0037 128.59);
  --accent-foreground: oklch(0.95 0.02 260);
  /* Destructive: maps to pink (consistent with app's danger UX) */
  --destructive: var(--pink);
  --destructive-foreground: oklch(1 0 0);
  /* Borders: very soft on dark */
  --border: oklch(0.28 0.015 260);
  --input: oklch(0.22 0.015 260);
  /* Ring uses primary for focus */
  --ring: oklch(0.9347 0.1739 127.82);
  --chart-1: oklch(0.9347 0.1739 127.82);
  --chart-2: oklch(0.78 0.1 260);
  --chart-3: oklch(0.68 0.12 30);
  --chart-4: oklch(0.72 0.18 150);
  --chart-5: oklch(0.5 0.08 260);
  --sidebar: var(--background);
  --sidebar-foreground: var(--foreground);
  --sidebar-primary: var(--primary);
  --sidebar-primary-foreground: var(--primary-foreground);
  --sidebar-accent: var(--accent);
  --sidebar-accent-foreground: var(--accent-foreground);
  --sidebar-border: var(--border);
  --sidebar-ring: var(--ring);
  /* Pill/IconButton tinted backgrounds — shared Tier-2 elevated-chrome value */
  --surface: oklch(0.2086 0.0019 106.57);
  --surface-foreground: var(--card-foreground);
  --code: var(--surface);
  --code-foreground: var(--surface-foreground);
  --code-highlight: oklch(0.2 0.01 260);
  --code-number: oklch(0.7 0.02 260);
  /* Selection uses primary */
  --selection: var(--primary);
  --selection-foreground: var(--primary-foreground);
  /* Brand = Primary action (tab highlight, logo accents) */
  --brand: var(--primary);
  --brand-foreground: var(--primary-foreground);
  /* Hot pink: primitive color token */
  --pink: oklch(0.6828 0.2464 350.78);
  /* Event date accent: hot pink */
  --event-date: var(--pink);
  /* Tag text color */
  --tag-foreground: oklch(0.9326 0.2075 121.72);
  /* MD3 surface-container: search bar background */
  --surface-container: oklch(0.85 0.005 145);
  /* MD3 on-surface-variant: hint text, icons on light surfaces */
  --on-surface-variant: oklch(0.365 0.017 290);
}

.dark {
  /* Mirror dark palette so `.dark` matches base */
  --background: oklch(0.09 0 0);
  --foreground: oklch(0.96 0.02 260);
  --card: oklch(0.16 0.01 260);
  --card-foreground: oklch(0.97 0.02 260);
  --card-event: oklch(0.2086 0.0019 106.57);
  --border-card-event: oklch(0.32 0.015 260 / 40%);
  --popover: oklch(0.2086 0.0019 106.57);
  --popover-foreground: oklch(0.97 0.02 260);
  --primary: oklch(0.9347 0.1739 127.82);
  --primary-foreground: oklch(0.09 0 0);
  --secondary: oklch(0.2 0.01 260);
  --secondary-foreground: oklch(0.9 0.02 260);
  --muted: oklch(0.18 0.01 260);
  --muted-foreground: oklch(0.7 0.02 260);
  --accent: oklch(0.3312 0.0037 128.59);
  --accent-foreground: oklch(0.95 0.02 260);
  --destructive: var(--pink);
  --destructive-foreground: oklch(1 0 0);
  --border: oklch(0.28 0.015 260);
  --input: oklch(0.22 0.015 260);
  --ring: oklch(0.9347 0.1739 127.82);
  --chart-1: oklch(0.9347 0.1739 127.82);
  --chart-2: oklch(0.78 0.1 260);
  --chart-3: oklch(0.68 0.12 30);
  --chart-4: oklch(0.72 0.18 150);
  --chart-5: oklch(0.5 0.08 260);
  --sidebar: var(--background);
  --sidebar-foreground: var(--foreground);
  --sidebar-primary: var(--primary);
  --sidebar-primary-foreground: var(--primary-foreground);
  --sidebar-accent: var(--accent);
  --sidebar-accent-foreground: var(--accent-foreground);
  --sidebar-border: var(--border);
  --sidebar-ring: var(--ring);
  --surface: oklch(0.2086 0.0019 106.57);
  --surface-foreground: var(--card-foreground);
  --code: var(--surface);
  --code-foreground: var(--surface-foreground);
  --code-highlight: oklch(0.2 0.01 260);
  --code-number: oklch(0.7 0.02 260);
  --selection: var(--primary);
  --selection-foreground: var(--primary-foreground);
  --brand: var(--primary);
  --brand-foreground: var(--primary-foreground);
  --tag-foreground: oklch(0.9326 0.2075 121.72);
  --surface-container: oklch(0.85 0.005 145);
  --on-surface-variant: oklch(0.365 0.017 290);
}

@layer base {
  * {
    @apply border-border outline-ring/50 font-sans;
  }
  ::selection {
    @apply bg-selection text-selection-foreground;
  }
  html {
    @apply overscroll-y-none scroll-smooth;
  }
  body {
    font-synthesis-weight: none;
    text-rendering: optimizeLegibility;
  }

  [data-slot="layout"] {
    @apply overscroll-none;
  }

  @supports (font: -apple-system-body) and (-webkit-appearance: none) {
    [data-wrapper] {
      @apply min-[1800px]:border-t;
    }
  }

  a:active,
  button:active {
    @apply opacity-60 md:opacity-100;
  }
}

@utility border-grid {
  @apply border-border/50 dark:border-border;
}

@utility section-soft {
  @apply from-background to-surface/40 dark:bg-background 3xl:fixed:bg-none bg-linear-to-b;
}

@utility theme-container {
  @apply font-sans;
}

@utility container-wrapper {
  @apply 3xl:fixed:max-w-[calc(var(--breakpoint-2xl)+2rem)] mx-auto w-full px-2;
}

@utility container {
  @apply 3xl:max-w-screen-2xl mx-auto max-w-[1400px] px-4 lg:px-8;
}

@utility no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

@utility border-ghost {
  @apply after:border-border relative after:absolute after:inset-0 after:border after:mix-blend-darken dark:after:mix-blend-lighten;
}

@utility step {
  counter-increment: step;
  @apply relative;

  &:before {
    @apply text-muted-foreground right-0 mr-2 hidden size-7 items-center justify-center rounded-full text-center -indent-px font-mono text-sm font-medium md:absolute;
    content: counter(step);
  }
}

@utility extend-touch-target {
  @media (pointer: coarse) {
    @apply relative touch-manipulation after:absolute after:-inset-2;
  }
}

@layer components {
  .steps {
    &:first-child {
      @apply mt-0!;
    }

    &:first-child > h3:first-child {
      @apply mt-0!;
    }

    > h3 {
      @apply mt-16!;
    }

    > h3 + p {
      @apply mt-2!;
    }
  }

  [data-rehype-pretty-code-figure] {
    background-color: var(--color-code);
    color: var(--color-code-foreground);
    border-radius: var(--radius-lg);
    border-width: 0px;
    border-color: var(--border);
    margin-top: calc(var(--spacing) * 6);
    overflow: hidden;
    font-size: var(--text-sm);
    outline: none;
    position: relative;
    @apply -mx-1 md:-mx-1;

    &:has([data-rehype-pretty-code-title]) [data-slot="copy-button"] {
      top: calc(var(--spacing) * 1.5) !important;
    }
  }

  [data-rehype-pretty-code-title] {
    border-bottom: color-mix(in oklab, var(--border) 30%, transparent);
    border-bottom-width: 1px;
    border-bottom-style: solid;
    padding-block: calc(var(--spacing) * 2.5);
    padding-inline: calc(var(--spacing) * 4);
    font-size: var(--text-sm);
    font-family: var(--font-mono);
    color: var(--color-code-foreground);
  }

  [data-line-numbers] {
    display: grid;
    min-width: 100%;
    white-space: pre;
    border: 0;
    background: transparent;
    padding: 0;
    counter-reset: line;
    box-decoration-break: clone;
  }

  [data-line-numbers] [data-line]::before {
    font-size: var(--text-sm);
    counter-increment: line;
    content: counter(line);
    display: inline-block;
    width: calc(var(--spacing) * 16);
    padding-right: calc(var(--spacing) * 6);
    text-align: right;
    color: var(--color-code-number);
    background-color: var(--color-code);
    position: sticky;
    left: 0;
  }

  [data-line-numbers] [data-highlighted-line][data-line]::before {
    background-color: var(--color-code-highlight);
  }

  [data-line] {
    padding-top: calc(var(--spacing) * 0.5);
    padding-bottom: calc(var(--spacing) * 0.5);
    min-height: calc(var(--spacing) * 1);
    width: 100%;
    display: inline-block;
  }

  [data-line] span {
    color: var(--shiki-light);

    @variant dark {
      color: var(--shiki-dark) !important;
    }
  }

  [data-highlighted-line],
  [data-highlighted-chars] {
    position: relative;
    background-color: var(--color-code-highlight);
  }

  [data-highlighted-line] {
    &:after {
      position: absolute;
      top: 0;
      left: 0;
      width: 2px;
      height: 100%;
      content: "";
      background-color: color-mix(
        in oklab,
        var(--muted-foreground) 50%,
        transparent
      );
    }
  }

  [data-highlighted-chars] {
    border-radius: var(--radius-sm);
    padding-inline: 0.3rem;
    padding-block: 0.1rem;
    font-family: var(--font-mono);
    font-size: 0.8rem;
  }
}

@layer components {
  .dialog-ring {
    @apply rounded-xl border-none bg-clip-padding shadow-2xl ring-4 ring-neutral-200/80 dark:bg-neutral-900 dark:ring-neutral-800;
  }
}
